'use client';

import 'swiper/css';
import 'swiper/css/navigation';

import { useGSAP } from '@gsap/react';
import { FC, useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Image from '@/components/Image';
import Link from '@/components/Link';
import { PartnershipSliderDto } from '@/components/Partnership/types';
import ArrowNext from '@/components/shared/Icon/ArrowNext';
import ArrowPrev from '@/components/shared/Icon/ArrowPrev';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: PartnershipSliderDto;
}

const SliderPartnership: FC<Props> = ({ data }) => {
	const { slides, title, subtitle } = data;
	const swiperRef = useRef<SwiperType | null>(null);
	const sectionRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const device = useDevice();

	const isDesktop = device === 'desktop';
	const slidesCount = slides.length;
	const showAllDesktop = isDesktop && slidesCount <= 3;

	useGSAP(
		() => {
			const wrapper = sectionRef.current;
			const titleEl = titleRef.current;
			const titleLines = titleEl?.querySelectorAll('p');

			if (!wrapper || !titleEl || !titleLines?.length) return;

			const mm = gsap.matchMedia();

			gsap.set(titleLines, {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			gsap.to(titleLines, {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
				duration: 1.1,
				ease: 'power3.out',
				stagger: 0.15,
				scrollTrigger: {
					trigger: titleEl,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			mm.add('(min-width: 992px)', () => {
				gsap.to(titleEl, {
					yPercent: -40,
					ease: 'none',
					scrollTrigger: {
						trigger: wrapper,
						start: 'top bottom',
						end: 'bottom top',
						scrub: true
					}
				});
			});

			return () => {
				mm.revert();
			};
		},
		{
			scope: sectionRef,
			dependencies: [device]
		}
	);

	return (
		<section ref={sectionRef} className={css.section}>
			<div className={css.container}>
				<div ref={titleRef} className={css.heading}>
					<Typography className={css.title} variant="h2" tag="h2">
						{title ? <StrapiRichContent content={title} /> : null}
					</Typography>

					{subtitle ? (
						<Typography className={css.subtitle} variant="body-m" tag="div">
							{subtitle ? <StrapiRichContent content={subtitle} /> : null}
						</Typography>
					) : null}
				</div>

				<div className={css.sliderWrap}>
					{device === 'desktop' && (
						<button
							type="button"
							className={`${css.navBtn} ${css.prev}`}
							aria-label="Previous slide"
							onClick={() => swiperRef.current?.slidePrev()}
						>
							<ArrowPrev size={24} />
						</button>
					)}
					{device === 'desktop' && (
						<button
							type="button"
							className={`${css.navBtn} ${css.next}`}
							aria-label="Next slide"
							onClick={() => swiperRef.current?.slideNext()}
						>
							<ArrowNext size={24} />
						</button>
					)}
					<Swiper
						loop
						speed={850}
						modules={[Navigation, A11y]}
						className={css.partnershipSwiper}
						spaceBetween={12}
						slidesPerView={1.2}
						breakpoints={{
							900: {
								slidesPerView: 2.2,
								spaceBetween: 20
							},
							1200: { slidesPerView: 2.9, spaceBetween: 32 }
						}}
						onSwiper={swiper => {
							swiperRef.current = swiper;
						}}
					>
						{slides.map(({ id, title, description, image, url }) => (
							<SwiperSlide key={`${id}-${title}`}>
								<Link href={url || '#'} className={css.card}>
									<Image
										src={image?.url ?? ''}
										alt={title}
										width={400}
										height={300}
										className={css.image}
									/>

									<div className={css.cardFooter}>
										{description ? <StrapiRichContent content={description} /> : null}
										<p className={css.cardTag}>{title}</p>
									</div>
								</Link>
							</SwiperSlide>
						))}
					</Swiper>
				</div>
			</div>
		</section>
	);
};

export default SliderPartnership;
